import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import type {
  LiteLlmStatus,
  McpStatusResponse,
  ModelsResponse,
  ToolsResponse,
} from './types';

/**
 * The small, non-streaming half of the plugin's backend API.
 *
 * The conversation itself does not go through here — `useChat` owns that
 * request, because it speaks the AG-UI protocol on the wire and handles
 * reconnection and cancellation for us.
 */
export interface LlmChatApiInterface {
  getModels(): Promise<ModelsResponse>;
  /** Proxy health plus the caller's key usage and limits. */
  getLiteLlmStatus(): Promise<LiteLlmStatus>;
  /** The configured MCP servers, each with the tools it serves. */
  getMcpStatus(): Promise<McpStatusResponse>;
  /** The same tools seen flat, across every server. */
  getTools(): Promise<ToolsResponse>;
  /** Absolute URL of the streaming endpoint, for `fetchServerSentEvents`. */
  getChatUrl(): Promise<string>;
}

export const llmChatApiRef = createApiRef<LlmChatApiInterface>().with({
  id: 'plugin.llm-chat.api',
});

export class LlmChatApi implements LlmChatApiInterface {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async get<T>(path: string): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('llm-chat');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(
        `Request to ${path} failed: ${response.status} ${response.statusText}${
          body ? ` — ${body}` : ''
        }`,
      );
    }
    return response.json() as Promise<T>;
  }

  async getModels(): Promise<ModelsResponse> {
    return this.get<ModelsResponse>('/models');
  }

  async getLiteLlmStatus(): Promise<LiteLlmStatus> {
    return this.get<LiteLlmStatus>('/litellm/status');
  }

  async getMcpStatus(): Promise<McpStatusResponse> {
    return this.get<McpStatusResponse>('/mcp/status');
  }

  async getTools(): Promise<ToolsResponse> {
    return this.get<ToolsResponse>('/tools');
  }

  async getChatUrl(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl('llm-chat')}/chat`;
  }
}
